import { useTranslation } from "react-i18next";
import PasswordField from "shared/components/form/PasswordField";
import SelectField from "shared/components/form/SelectField";
import TextField from "shared/components/form/TextField";
import { CreateUserFormT } from "shared/form-values/users";
import { roleOptions } from "./constants";
import { Flex, FormLabel } from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import { useMemo, useState } from "react";
import { ResourceSelectLabel } from "shared/components/ResourceSelectLabel";
import { useFormContext } from "react-hook-form";
import { OneUserT } from "shared/api/types/users";
import { useGetResources } from "shared/api/requests/resources";
import { useDebounce } from "shared/hooks/useDebounce";

interface IProps {
  defaultResources?: OneUserT['response']['resources']
}

const UserForm = ({ defaultResources }: IProps) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext<CreateUserFormT>();
  const [resourceInputValue, setResourceInputValue] = useState('');
  const debouncedResourceInputValue = useDebounce<string>(resourceInputValue, 500);

  const { data: resourcesData, isLoading } = useGetResources({
    params: {
      size: 100,
      filter: null,
      search: debouncedResourceInputValue,
    }
  });

  const resourcesList = useMemo(() => {
    const totalData = resourcesData?.pages
      ?.flat()
      ?.map((item) => item.data)
      ?.flat();

    const res: OneUserT["response"]["resources"] = [];

    totalData?.forEach((i) => res.push(i));
    return res;
  }, [resourcesData]);

  const filteredResourcesList = useMemo(() => {
    return resourcesList?.filter((item) => {
      return item?.resource?.name?.toLowerCase()?.includes(debouncedResourceInputValue.toLowerCase());
    }) ?? []

  }, [debouncedResourceInputValue, resourcesList]);
  

  return (
    <Flex direction="column" gap={4} w={{ base: "100%", md: "80%" }}>
      <TextField<CreateUserFormT>
        name="email"
        type="email"
        label={t("users.table.email")}
        placeholder={t("users.table.email")}
        required
      />
      <TextField<CreateUserFormT>
        name="name"
        label="Name"
        placeholder="Enter name"
        required
      />
      <TextField<CreateUserFormT>
        name="surname"
        label="Surname"
        placeholder="Enter surname"
        required
      />
      <SelectField<CreateUserFormT>
        options={roleOptions}
        name="role"
        label={t("users.table.role")}
        placeholder={t("users.role")}
        required
      />
      <PasswordField<CreateUserFormT> name="password" label="Password" />

      <FormLabel>Resources</FormLabel>
      <Select
        isMulti
        defaultValue={defaultResources ?? []}
        name="resources"
        placeholder={"Add resources"}
        isLoading={isLoading}
        options={filteredResourcesList}
        inputValue={resourceInputValue}
        onInputChange={(value) => {
          setResourceInputValue(value)
        }}
        getOptionLabel={(option) => option.resource.name}
        getOptionValue={(option) => option.resource.id}
        onChange={(value) => {
          setValue(
            "resources",
            value.map((item) => ({
              resourceId: item.resource.id,
              resourceType: item.resource.type,
            })),
          );
        }}
        formatOptionLabel={(option) => {
          return (
            <ResourceSelectLabel
              resourceName={option.resource.name}
              resourceType={option.resource.type}
            />
          );
        }}
      />
    </Flex>
  );
};

export default UserForm;
